import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { AuthService } from './core/auth.service';
import { authGuard, guestGuard, notTechnicianOnlyGuard, pendingOrganizationSelectionGuard } from './core/auth.guard';
import { bulkWizardLeaveGuard } from './features/admin/buildings/detail/structure/location-bulk-leave.service';
import { settingsGuard } from './core/settings-access';
import { accessOf, guardsFor } from './core/route-access';
import { managementDashboardMatch, providerDashboardMatch } from './features/dashboard/management/dashboard-access';
import { contractUnsavedGuard } from './features/service-contracts/contract-unsaved.guard';

/** Issue #600: a route's guards and `data.roles` come from its entry in ROUTE_ACCESS (core/route-access.ts), the same map the sidebar reads. `data` adds route-specific values. */
const access = (path: string, data: Record<string, unknown> = {}) => {
  const rule = accessOf(path);
  return { canActivate: [authGuard, ...guardsFor(rule)], data: { ...(rule.roles ? { roles: rule.roles } : {}), ...data } };
};

export const routes: Routes = [
  { path: 'login', canActivate: [guestGuard], loadComponent: () => import('./features/login/login.component').then((m) => m.LoginComponent) },
  {
    path: 'register',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/register-organization/register-organization.component').then((m) => m.RegisterOrganizationComponent)
  },
  {
    path: 'forgot-password',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent)
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./features/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent)
  },
  {
    path: 'accept-invitation',
    loadComponent: () => import('./features/accept-invitation/accept-invitation.component').then((m) => m.AcceptInvitationComponent)
  },
  {
    path: 'select-organization',
    canActivate: [pendingOrganizationSelectionGuard],
    loadComponent: () =>
      import('./features/select-organization/select-organization.component').then((m) => m.SelectOrganizationComponent)
  },
  {
    path: 'sso-callback',
    loadComponent: () => import('./features/sso-callback/sso-callback.component').then((m) => m.SsoCallbackComponent)
  },
  {
    // docs/design/40 §6 - the page the emailed registration link opens; the organization is created from it.
    path: 'confirm-registration',
    loadComponent: () => import('./features/confirm-registration/confirm-registration.component').then((m) => m.ConfirmRegistrationComponent)
  },
  {
    path: 'organizations/claim',
    loadComponent: () => import('./features/claim-organization/claim-organization.component').then((m) => m.ClaimOrganizationComponent)
  },
  {
    // docs/design/42 - the provider dashboard for whoever runs a service provider's jobs (provider organization + the jobs coordinator rule); the management dashboard and the tile page below are for everyone else.
    path: 'dashboard',
    canActivate: [authGuard],
    canMatch: [providerDashboardMatch],
    loadComponent: () => import('./features/dashboard/provider/provider-dashboard.component').then((m) => m.ProviderDashboardComponent)
  },
  {
    // docs/design/37 - the management dashboard for SystemAdmin / FacilityManager; the tile page below for every other role.
    path: 'dashboard',
    canActivate: [authGuard],
    canMatch: [managementDashboardMatch],
    loadComponent: () => import('./features/dashboard/management/management-dashboard.component').then((m) => m.ManagementDashboardComponent)
  },
  {
    path: 'dashboard',
    // Issue #592: a technician-only user has no Dashboard; they are sent to Work Orders.
    canActivate: [authGuard, notTechnicianOnlyGuard],
    loadComponent: () => import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent)
  },
  {
    path: 'calendar',
    ...access('calendar'),
    loadComponent: () => import('./features/calendar/calendar.component').then((m) => m.CalendarComponent)
  },
  {
    path: 'preventive-maintenance',
    ...access('preventive-maintenance'),
    loadComponent: () =>
      import('./features/preventive-maintenance/preventive-maintenance.component').then((m) => m.PreventiveMaintenanceComponent)
  },
  {
    path: 'preventive-maintenance/new',
    ...access('preventive-maintenance/new'),
    loadComponent: () =>
      import('./features/preventive-maintenance/form/maintenance-plan-form.component').then((m) => m.MaintenancePlanFormComponent)
  },
  {
    path: 'preventive-maintenance/:id/edit',
    ...access('preventive-maintenance/:id/edit'),
    loadComponent: () =>
      import('./features/preventive-maintenance/form/maintenance-plan-form.component').then((m) => m.MaintenancePlanFormComponent)
  },
  {
    path: 'preventive-maintenance/:id',
    ...access('preventive-maintenance/:id'),
    loadComponent: () =>
      import('./features/preventive-maintenance/detail/maintenance-plan-detail.component').then((m) => m.MaintenancePlanDetailComponent)
  },
  {
    path: 'work-orders',
    // Issue #610: a technician-only user works from My Tasks; the list is not theirs (a work order's detail below stays open).
    ...access('work-orders'),
    loadComponent: () => import('./features/work-orders/work-orders.component').then((m) => m.WorkOrdersComponent)
  },
  {
    path: 'work-orders/new',
    ...access('work-orders/new'),
    loadComponent: () => import('./features/work-orders/new/new-work-order.component').then((m) => m.NewWorkOrderComponent)
  },
  {
    path: 'work-orders/:id',
    ...access('work-orders/:id'),
    loadComponent: () => import('./features/work-order-detail/work-order-detail.component').then((m) => m.WorkOrderDetailComponent)
  },
  {
    path: 'maintenance-requests',
    ...access('maintenance-requests'),
    loadComponent: () =>
      import('./features/maintenance-requests/maintenance-requests.component').then((m) => m.MaintenanceRequestsComponent)
  },
  {
    path: 'maintenance-requests/new',
    ...access('maintenance-requests/new'),
    loadComponent: () =>
      import('./features/maintenance-requests/new/new-maintenance-request.component').then((m) => m.NewMaintenanceRequestComponent)
  },
  {
    path: 'maintenance-requests/:id',
    ...access('maintenance-requests/:id'),
    loadComponent: () =>
      import('./features/maintenance-requests/detail/maintenance-request-detail.component').then((m) => m.MaintenanceRequestDetailComponent)
  },
  {
    path: 'parts',
    ...access('parts'),
    loadComponent: () => import('./features/parts/parts.component').then((m) => m.PartsComponent)
  },
  {
    path: 'parts/new',
    ...access('parts/new'),
    loadComponent: () => import('./features/parts/new/new-part.component').then((m) => m.NewPartComponent)
  },
  {
    path: 'parts/categories',
    ...access('parts/categories'),
    loadComponent: () => import('./features/parts/categories/part-categories.component').then((m) => m.PartCategoriesComponent)
  },
  {
    path: 'parts/:id',
    ...access('parts/:id'),
    loadComponent: () => import('./features/parts/detail/part-detail.component').then((m) => m.PartDetailComponent)
  },
  {
    path: 'stores',
    ...access('stores'),
    loadComponent: () => import('./features/stores/stores.component').then((m) => m.StoresComponent)
  },
  {
    path: 'stores/new',
    ...access('stores/new'),
    loadComponent: () => import('./features/stores/new/new-store.component').then((m) => m.NewStoreComponent)
  },
  {
    path: 'stores/:id',
    ...access('stores/:id'),
    loadComponent: () => import('./features/stores/detail/store-detail.component').then((m) => m.StoreDetailComponent)
  },
  {
    path: 'reports',
    ...access('reports'),
    loadComponent: () => import('./features/reports/reports.component').then((m) => m.ReportsComponent)
  },
  {
    path: 'reports/most-maintained-locations',
    ...access('reports/most-maintained-locations'),
    loadComponent: () =>
      import('./features/reports/most-maintained-locations/most-maintained-locations.component').then((m) => m.MostMaintainedLocationsComponent)
  },
  {
    path: 'inventory',
    ...access('inventory'),
    loadComponent: () => import('./features/inventory/inventory.component').then((m) => m.InventoryComponent)
  },
  {
    path: 'inventory/usage-report',
    ...access('inventory/usage-report'),
    loadComponent: () =>
      import('./features/inventory/usage-report/usage-report.component').then((m) => m.InventoryUsageReportComponent)
  },
  {
    path: 'inventory/transactions',
    ...access('inventory/transactions'),
    loadComponent: () =>
      import('./features/inventory/transactions/stock-transactions.component').then((m) => m.StockTransactionsComponent)
  },
  {
    path: 'inventory/adjustments',
    ...access('inventory/adjustments'),
    loadComponent: () =>
      import('./features/inventory/adjustment/stock-adjustment.component').then((m) => m.StockAdjustmentComponent)
  },
  {
    path: 'purchase-orders',
    ...access('purchase-orders'),
    loadComponent: () => import('./features/purchase-orders/purchase-orders.component').then((m) => m.PurchaseOrdersComponent)
  },
  {
    path: 'purchase-orders/new',
    ...access('purchase-orders/new'),
    loadComponent: () =>
      import('./features/purchase-orders/new/new-purchase-order.component').then((m) => m.NewPurchaseOrderComponent)
  },
  {
    path: 'purchase-orders/:id',
    ...access('purchase-orders/:id'),
    loadComponent: () =>
      import('./features/purchase-orders/detail/purchase-order-detail.component').then((m) => m.PurchaseOrderDetailComponent)
  },
  {
    path: 'jobs',
    ...access('jobs'),
    loadComponent: () => import('./features/jobs/jobs.component').then((m) => m.JobsComponent)
  },
  {
    path: 'jobs/:id',
    ...access('jobs/:id'),
    loadComponent: () => import('./features/jobs/detail/job-detail.component').then((m) => m.JobDetailComponent)
  },
  {
    path: 'my-approvals',
    ...access('my-approvals'),
    loadComponent: () => import('./features/my-approvals/my-approvals.component').then((m) => m.MyApprovalsComponent)
  },
  {
    // The notification deep link: same page, with that task highlighted above the list.
    path: 'my-approvals/:taskId',
    ...access('my-approvals/:taskId'),
    loadComponent: () => import('./features/my-approvals/my-approvals.component').then((m) => m.MyApprovalsComponent)
  },
  {
    path: 'my-tasks',
    // The same rule as the sidebar link: the roles of the item, and not an in-organization vendor.
    ...access('my-tasks'),
    loadComponent: () => import('./features/my-tasks/my-tasks.component').then((m) => m.MyTasksComponent)
  },
  {
    // Issue #611: one job task of the technician, behind the same guards as the list.
    path: 'my-tasks/:id',
    ...access('my-tasks/:id'),
    loadComponent: () => import('./features/my-tasks/detail/my-task-detail.component').then((m) => m.MyTaskDetailComponent)
  },
  // docs/design/36 §9, docs/design/41 S8: the contracts an organization holds as the CLIENT live under /contracts (any organization type, a provider included) and the ones it was offered as the PROVIDER (its administrator: the API refuses every other provider role) under /provider-contracts, so a provider that is both sides has two separate lists; the old /service-contracts address still works and goes to the one for the signed-in organization.
  {
    path: 'contracts',
    ...access('contracts'),
    loadComponent: () => import('./features/service-contracts/contracts-list.component').then((m) => m.ContractsListComponent)
  },
  {
    path: 'contracts/new',
    ...access('contracts/new'),
    loadComponent: () => import('./features/service-contracts/contract-new.component').then((m) => m.ContractNewComponent)
  },
  {
    path: 'contracts/:id',
    ...access('contracts/:id'),
    canDeactivate: [contractUnsavedGuard],
    loadComponent: () => import('./features/service-contracts/contract-detail.component').then((m) => m.ContractDetailComponent)
  },
  {
    path: 'provider-contracts',
    ...access('provider-contracts', { contractSide: 'Provider' }),
    loadComponent: () => import('./features/service-contracts/contracts-list.component').then((m) => m.ContractsListComponent)
  },
  {
    path: 'provider-contracts/:id',
    ...access('provider-contracts/:id'),
    canDeactivate: [contractUnsavedGuard],
    loadComponent: () => import('./features/service-contracts/contract-detail.component').then((m) => m.ContractDetailComponent)
  },
  {
    path: 'service-contracts',
    redirectTo: () => (inject(AuthService).isProviderOrganization() ? '/provider-contracts' : '/contracts')
  },
  {
    path: 'service-contracts/:id',
    // The side is the viewer's side on THAT contract, which only the contract says: the contract page moves itself to /provider-contracts/:id when the viewer is the provider (docs/design/41 S8).
    redirectTo: ({ params }) => `/contracts/${params['id']}`
  },
  {
    path: 'vendors',
    ...access('vendors'),
    loadComponent: () => import('./features/vendors/vendors.component').then((m) => m.VendorsComponent)
  },
  {
    path: 'vendors/new',
    ...access('vendors/new'),
    loadComponent: () => import('./features/vendors/new/new-vendor.component').then((m) => m.NewVendorComponent)
  },
  {
    // Plan §3b: a pending invitation has no vendor id; the same page shows it (Details only).
    path: 'vendors/invitations/:id',
    ...access('vendors/invitations/:id'),
    loadComponent: () => import('./features/vendors/detail/vendor-detail.component').then((m) => m.VendorDetailComponent)
  },
  {
    path: 'vendors/:id',
    ...access('vendors/:id'),
    loadComponent: () => import('./features/vendors/detail/vendor-detail.component').then((m) => m.VendorDetailComponent)
  },
  {
    path: 'assets',
    ...access('assets'),
    loadComponent: () => import('./features/assets/assets.component').then((m) => m.AssetsComponent)
  },
  {
    path: 'asset-categories',
    ...access('asset-categories'),
    loadComponent: () => import('./features/asset-categories/asset-categories.component').then((m) => m.AssetCategoriesComponent)
  },
  {
    path: 'assets/new',
    ...access('assets/new'),
    loadComponent: () => import('./features/assets/new/new-asset.component').then((m) => m.NewAssetComponent)
  },
  {
    path: 'assets/:id',
    ...access('assets/:id'),
    loadComponent: () => import('./features/assets/detail/asset-detail.component').then((m) => m.AssetDetailComponent)
  },
  {
    path: 'admin/buildings',
    ...access('admin/buildings'),
    loadComponent: () => import('./features/admin/buildings/buildings.component').then((m) => m.BuildingsComponent)
  },
  {
    path: 'admin/projects',
    ...access('admin/projects'),
    loadComponent: () => import('./features/admin/projects/projects.component').then((m) => m.ProjectsComponent)
  },
  {
    path: 'admin/buildings/new',
    ...access('admin/buildings/new'),
    loadComponent: () => import('./features/admin/buildings/new/new-building.component').then((m) => m.NewBuildingComponent)
  },
  {
    path: 'admin/buildings/:id',
    ...access('admin/buildings/:id'),
    canDeactivate: [bulkWizardLeaveGuard],
    loadComponent: () => import('./features/admin/buildings/detail/building-detail.component').then((m) => m.BuildingDetailComponent)
  },
  {
    path: 'admin/priority-matrix',
    ...access('admin/priority-matrix'),
    loadComponent: () => import('./features/admin/priority-matrix/priority-matrix.component').then((m) => m.PriorityMatrixComponent)
  },
  {
    path: 'admin/maintenance-categories',
    ...access('admin/maintenance-categories'),
    loadComponent: () =>
      import('./features/admin/maintenance-categories/maintenance-categories.component').then((m) => m.MaintenanceCategoriesComponent)
  },
  {
    path: 'admin/users',
    ...access('admin/users'),
    loadComponent: () => import('./features/admin/users/admin-users.component').then((m) => m.AdminUsersComponent)
  },
  {
    path: 'admin/users/new',
    ...access('admin/users/new'),
    loadComponent: () => import('./features/admin/users/new/new-user.component').then((m) => m.NewUserComponent)
  },
  {
    path: 'admin/users/:id/transfer',
    ...access('admin/users/:id/transfer'),
    loadComponent: () => import('./features/admin/users/transfer/technician-transfer-page.component').then((m) => m.TechnicianTransferPageComponent)
  },
  {
    path: 'admin/users/:id',
    ...access('admin/users/:id'),
    loadComponent: () => import('./features/admin/users/detail/admin-user-detail.component').then((m) => m.AdminUserDetailComponent)
  },
  {
    path: 'admin/audit-log',
    ...access('admin/audit-log'),
    loadComponent: () => import('./features/admin/audit-log/audit-log.component').then((m) => m.AuditLogComponent)
  },
  {
    path: 'admin/imports',
    ...access('admin/imports'),
    loadComponent: () => import('./features/admin/imports/imports.component').then((m) => m.ImportsComponent)
  },
  {
    path: 'admin/notification-templates/new',
    ...access('admin/notification-templates/new'),
    loadComponent: () =>
      import('./features/admin/notification-templates/form/notification-template-form.component').then((m) => m.NotificationTemplateFormComponent)
  },
  {
    path: 'admin/notification-templates/:id/edit',
    ...access('admin/notification-templates/:id/edit'),
    loadComponent: () =>
      import('./features/admin/notification-templates/form/notification-template-form.component').then((m) => m.NotificationTemplateFormComponent)
  },
  {
    path: 'admin/notification-templates',
    ...access('admin/notification-templates'),
    loadComponent: () =>
      import('./features/admin/notification-templates/notification-templates.component').then((m) => m.NotificationTemplatesComponent)
  },
  {
    path: 'admin/notification-log',
    ...access('admin/notification-log'),
    loadComponent: () =>
      import('./features/admin/notification-log/notification-log.component').then((m) => m.NotificationLogComponent)
  },
  {
    path: 'settings',
    canActivate: [authGuard, settingsGuard],
    loadComponent: () => import('./features/settings/settings.component').then((m) => m.SettingsComponent)
  },
  {
    path: 'profile',
    ...access('profile'),
    loadComponent: () => import('./features/profile/profile.component').then((m) => m.ProfileComponent)
  },
  {
    path: 'admin/organizations',
    ...access('admin/organizations'),
    loadComponent: () => import('./features/admin/organizations/organizations.component').then((m) => m.OrganizationsComponent)
  },
  {
    path: 'admin/organizations/:id',
    ...access('admin/organizations/:id'),
    loadComponent: () => import('./features/admin/organizations/detail/organization-detail.component').then((m) => m.OrganizationDetailComponent)
  },
  {
    path: 'admin/scopes-of-work',
    ...access('admin/scopes-of-work'),
    loadComponent: () => import('./features/admin/scopes-of-work/scopes-of-work.component').then((m) => m.ScopesOfWorkComponent)
  },
  {
    path: 'admin/agents',
    ...access('admin/agents'),
    loadComponent: () => import('./features/admin/agents/agents.component').then((m) => m.AgentsComponent)
  },
  {
    path: 'admin/approval-routing',
    ...access('admin/approval-routing'),
    loadComponent: () =>
      import('./features/admin/approval-routing/list/approval-routing-list.component').then((m) => m.ApprovalRoutingListComponent)
  },
  {
    path: 'admin/approval-routing/:actionKey',
    ...access('admin/approval-routing/:actionKey'),
    loadComponent: () =>
      import('./features/admin/approval-routing/detail/approval-routing-detail.component').then(
        (m) => m.ApprovalRoutingDetailComponent
      )
  },
  {
    path: '',
    pathMatch: 'full',
    ...access(''),
    loadComponent: () => import('./features/home/home-redirect.component').then((m) => m.HomeRedirectComponent)
  },
  { path: '**', redirectTo: '' }
];
